/**
 * Module pilote — Client / Mes opérations
 * 22 slides : Cover, Intro, Concepts, Screenshots, Étapes, Quiz, Takeaways, CTA
 */
const moduleClientMesOperations = {
  id: 'client-mes-operations',
  population: 'client',
  order: 2,
  category: 'Opérations',
  title: 'Mes opérations',
  shortTitle: 'Mes opérations',
  subtitle: 'Créer, suivre et piloter vos demandes de transport de A à Z',
  icon: 'Truck',
  duration: '25 min',
  level: 'Débutant → Intermédiaire',
  audience: 'Expéditeurs & gestionnaires logistique',
  pdfUrl: '/api/uploads/docs/training/client-mes-operations.pdf',
  pptxUrl: '/api/uploads/docs/training/client-mes-operations.pptx',
  slides: [
    {
      type: 'cover',
      eyebrow: 'Module 02 — Client',
      title: 'Mes opérations',
      subtitle: 'Maîtriser le cycle complet d\'une demande de transport',
      icon: 'Truck',
      duration: '25 min',
      audience: 'Expéditeurs',
      level: 'Débutant → Intermédiaire',
    },
    {
      type: 'intro',
      title: 'Ce que vous saurez faire à la fin',
      intro: "Ce module vous guide pas-à-pas dans la création, le suivi et la clôture d'une demande de transport sur Baobab Connect.",
      objectives: [
        'Créer une demande de transport spot en moins de 3 minutes',
        'Suivre le statut en temps réel et anticiper les blocages',
        'Comprendre les phases : préparation, exécution, terminée, annulée',
        'Filtrer, dupliquer, annuler une demande efficacement',
        'Utiliser les vues sauvegardées pour gagner du temps',
        'Exporter vos opérations en CSV pour reporting interne',
      ],
    },
    {
      type: 'concept',
      eyebrow: 'Vocabulaire',
      icon: 'BookOpen',
      title: 'Le cycle de vie d\'une opération',
      lede: "Toute demande passe par une suite de statuts. Comprendre ces phases vous aide à anticiper les actions à mener.",
      bullets: [
        '🟦 Préparation : Enregistrée → En attente de devis → À valider → Négociation → Devis accepté',
        '🟧 Exécution : Planifiée → Prête au chargement → Chargement → En route → Livrée',
        '🟩 Terminée : En attente paiement → Payée → Terminée',
        '🟥 Annulée : Par client, par admin, ou expirée',
      ],
      note: "Cliquez sur l'icône Légende en haut à droite de la page pour afficher la signification de chaque badge de statut.",
    },
    {
      type: 'screenshot',
      eyebrow: 'Vue d\'ensemble',
      title: 'La page « Mes opérations »',
      callouts: [
        { label: 'KPI Header', text: 'Total, En préparation, En cours, Terminées — avec évolution vs mois précédent.' },
        { label: 'Recherche avancée', text: 'Recherche full-text + filtres avancés (date, statut, montant, trajet).' },
        { label: 'Onglets Phase', text: 'Toutes / Préparation / En cours / Terminées / Annulées avec compteurs.' },
        { label: 'Vues sauvegardées', text: 'Enregistrez vos filtres récurrents pour les retrouver en un clic.' },
        { label: 'Liste / Cartes', text: 'Basculez entre vue liste (dense) et vue cartes (visuel).' },
      ],
    },
    {
      type: 'steps',
      eyebrow: 'Tutoriel pas-à-pas',
      title: 'Créer une demande de transport spot',
      steps: [
        { title: 'Cliquer sur « + Nouvelle demande »', detail: 'Bouton vert en haut à droite de toutes les pages opérations.' },
        { title: 'Saisir le trajet', detail: 'Départ et arrivée avec autocomplétion CEDEAO (15 pays supportés).' },
        { title: 'Préciser la marchandise', detail: 'Type, poids, nombre de camions ou de conteneurs.' },
        { title: 'Choisir le type de véhicule', detail: 'Plateau, Citerne, Frigo, Porte-conteneurs… (catalogue contractuel).' },
        { title: 'Indiquer la date souhaitée', detail: 'Date de chargement ; la date de livraison estimée est calculée automatiquement.' },
        { title: 'Soumettre la demande', detail: 'Un numéro de référence BAO-OP-… est attribué et notifié à votre opérateur.' },
      ],
    },
    {
      type: 'concept',
      eyebrow: 'Devis & validation',
      icon: 'DollarSign',
      title: 'Le workflow de devis',
      lede: 'Une fois la demande soumise, votre opérateur Baobab vous transmet un devis. Voici les actions possibles.',
      bullets: [
        'Accepter le devis : l\'opération passe en « Planifiée » et un contrat spot est signé.',
        'Négocier : ouvrez une discussion pour ajuster le prix ou les conditions.',
        'Refuser : la demande est archivée (vous pouvez la dupliquer plus tard).',
        'Demander une révision : modification du trajet, de la marchandise ou du véhicule.',
      ],
      note: 'Astuce : utilisez la messagerie intégrée pour garder l\'historique des échanges sur chaque opération.',
    },
    {
      type: 'screenshot',
      eyebrow: 'Détail opération',
      title: 'La fiche détaillée d\'une opération',
      callouts: [
        { label: 'Timeline statuts', text: 'Visualisation chronologique avec timestamps de chaque étape.' },
        { label: 'Documents joints', text: 'BL, BLT, photos chargement/déchargement, factures.' },
        { label: 'Suivi GPS', text: 'Position en temps réel quand le véhicule est tracké.' },
        { label: 'Messagerie', text: 'Chat avec votre opérateur dédié.' },
        { label: 'Actions', text: 'Dupliquer, annuler, télécharger les pièces, demander une révision.' },
      ],
    },
    {
      type: 'steps',
      eyebrow: 'Cas d\'usage',
      title: 'Dupliquer une opération récurrente',
      steps: [
        { title: 'Ouvrir l\'opération source', detail: 'Depuis la liste, cliquez sur la ligne ou l\'icône Eye.' },
        { title: 'Menu Actions → Dupliquer', detail: 'Bouton ⋮ en haut de la fiche détaillée.' },
        { title: 'Ajuster les champs', detail: 'Trajet, marchandise et date pré-remplis ; vous pouvez tout modifier.' },
        { title: 'Soumettre la copie', detail: 'Un nouveau numéro BAO-OP-… est généré.' },
      ],
    },
    {
      type: 'concept',
      eyebrow: 'Recherche & filtres',
      icon: 'ListChecks',
      title: 'Trouver une opération en 5 secondes',
      lede: 'La page propose 3 niveaux de recherche complémentaires :',
      bullets: [
        '🔍 Barre de recherche : full-text sur référence, trajet, marchandise, opérateur.',
        '🎯 Onglets Phase : filtre rapide par statut groupé.',
        '⚙️ Filtres avancés : date min/max, statut financier, type d\'opération, montant min/max.',
        '🔖 Vues sauvegardées : combinaisons de filtres + tri persistées par utilisateur.',
      ],
      note: 'Le tri se règle indépendamment (date, prix, distance) via le menu « Date (récent → ancien) ».',
    },
    {
      type: 'steps',
      eyebrow: 'Productivité',
      title: 'Créer & utiliser une vue sauvegardée',
      steps: [
        { title: 'Appliquer vos filtres', detail: 'Ex : Phase = En cours + Période = 30 derniers jours.' },
        { title: 'Cliquer sur « Enregistrer la vue »', detail: 'Bouton à droite de la barre de filtres.' },
        { title: 'Nommer la vue', detail: 'Ex : « Mes urgences semaine ».' },
        { title: 'Retrouver la vue', detail: 'Liste déroulante « Mes vues » à gauche de la barre.' },
        { title: 'Supprimer une vue', detail: 'Hover sur le nom → icône poubelle.' },
      ],
    },
    {
      type: 'checklist',
      title: 'Avant de créer votre demande : la checklist',
      items: [
        'J\'ai vérifié que l\'adresse de départ est précise (numéro, quartier, ville)',
        'J\'ai validé le poids estimé et le nombre de camions/conteneurs',
        'J\'ai choisi le bon type de véhicule (consultez le catalogue si besoin)',
        'J\'ai prévu une marge de 24 h sur la date de chargement souhaitée',
        'J\'ai préparé les coordonnées du contact sur place (chargement & livraison)',
        'J\'ai consulté le module Finances pour mon solde de trésorerie',
      ],
    },
    {
      type: 'comparison',
      title: 'Bonnes pratiques vs erreurs courantes',
      do: [
        'Saisir le trajet avec autocomplétion (évite les fautes de frappe)',
        'Ajouter une note dans le champ « Instructions spéciales »',
        'Joindre une copie du bon de commande dès la création',
        'Valider le devis dans les 48 h pour bloquer la disponibilité véhicule',
        'Activer les notifications email/SMS pour les changements de statut',
      ],
      dont: [
        'Ne saisissez pas plusieurs demandes identiques (utilisez Dupliquer)',
        'Ne soumettez pas sans préciser le poids (cela bloque la facturation)',
        'N\'attendez pas la veille pour valider un devis sur trajet longue distance',
        'Ne supprimez pas une demande en cours d\'exécution (utilisez Annuler)',
        'Ne contournez pas le devis via WhatsApp (perte d\'historique légal)',
      ],
    },
    {
      type: 'concept',
      eyebrow: 'Cas particulier',
      icon: 'AlertTriangle',
      title: 'Annuler une demande : quand et comment',
      lede: 'L\'annulation est possible jusqu\'au statut « Chargement en cours ». Au-delà, contactez votre opérateur.',
      bullets: [
        '✅ Avant validation du devis : annulation gratuite, automatique.',
        '⚠️ Après acceptation du devis : possible avec frais éventuels selon contrat.',
        '🚫 Pendant l\'exécution (chargement, route) : appel obligatoire au support.',
        '📞 Support : disponible 24/7 via le bouton Messagerie ou +225 XX XX XX XX.',
      ],
      note: "L'annulation est tracée dans l'historique et peut être justifiée par un commentaire libre.",
    },
    {
      type: 'screenshot',
      eyebrow: 'Suivi temps réel',
      title: 'Suivre le véhicule sur la carte',
      callouts: [
        { label: 'Carte interactive', text: 'Position GPS rafraîchie toutes les 30 secondes quand le véhicule est connecté.' },
        { label: 'ETA dynamique', text: 'Estimation d\'arrivée recalculée selon trafic et conditions météo.' },
        { label: 'Alertes proximité', text: 'Notification quand le camion est à 10 km de l\'arrivée.' },
        { label: 'Historique trajet', text: 'Replay complet du parcours après livraison.' },
      ],
    },
    {
      type: 'quiz',
      title: 'Vérification rapide',
      question: 'Vous venez de valider un devis. Quel est le prochain statut affiché sur votre opération ?',
      options: [
        'En attente de devis',
        'Planifiée',
        'En route',
        'Terminée',
      ],
      answerIndex: 1,
      explanation: 'Après validation du devis, l\'opération passe en « Planifiée ». Un véhicule est alors réservé et un opérateur assigné.',
    },
    {
      type: 'quiz',
      title: 'Quiz #2',
      question: 'Vous voulez retrouver rapidement toutes vos opérations en retard de paiement. La meilleure méthode est :',
      options: [
        'Faire défiler manuellement la liste complète',
        'Demander à l\'opérateur par WhatsApp',
        'Créer une vue sauvegardée avec filtre « Statut financier = En retard »',
        'Exporter en CSV et trier dans Excel',
      ],
      answerIndex: 2,
      explanation: 'Les vues sauvegardées sont la fonctionnalité dédiée pour ce type de besoin récurrent : 2 clics suffisent ensuite.',
    },
    {
      type: 'steps',
      eyebrow: 'Export & reporting',
      title: 'Exporter ses opérations en CSV',
      steps: [
        { title: 'Appliquer les filtres souhaités', detail: 'Période, statut, entreprise…' },
        { title: 'Cliquer sur « Exporter CSV »', detail: 'En haut à droite de la barre de filtres.' },
        { title: 'Choisir le format', detail: 'Toutes les colonnes ou export personnalisé.' },
        { title: 'Télécharger', detail: 'Le fichier .csv est généré et téléchargé instantanément.' },
        { title: 'Importer dans votre outil', detail: 'Excel, Google Sheets, Sage, Odoo, etc.' },
      ],
    },
    {
      type: 'concept',
      eyebrow: 'Multi-utilisateurs',
      icon: 'Users',
      title: 'Mes opérations vs Toutes les opérations de l\'entreprise',
      lede: 'Si plusieurs collaborateurs partagent un compte entreprise, vous pouvez basculer la vue.',
      bullets: [
        '🟢 « Mes opérations » : seulement celles que vous avez créées.',
        '🔵 « Toutes l\'agence » : visibles si votre rôle l\'autorise (admin entreprise).',
        '🔒 Permissions configurables : voir, créer, modifier, annuler — par utilisateur.',
        '👀 Audit : chaque action est tracée avec l\'identifiant de l\'utilisateur.',
      ],
    },
    {
      type: 'takeaways',
      title: 'Les 5 réflexes à retenir',
      items: [
        'Créer une demande prend < 3 minutes si toutes les infos sont prêtes.',
        'Valider le devis dans les 48 h pour sécuriser le véhicule.',
        'Utiliser les vues sauvegardées pour vos consultations récurrentes.',
        'Suivre en temps réel via la fiche détaillée ou le Suivi temps réel.',
        'Exporter en CSV pour intégrer vos données dans votre ERP.',
      ],
    },
    {
      type: 'screenshot',
      eyebrow: 'Pour aller plus loin',
      title: 'Lien avec les autres modules',
      callouts: [
        { label: 'Module Finances', text: 'Voir toutes vos factures, paiements et soldes en un clic.' },
        { label: 'Module Suivi temps réel', text: 'Carte multi-véhicules pour vue globale de vos flux.' },
        { label: 'Module Calendrier', text: 'Vue calendaire pour planifier vos chargements à venir.' },
        { label: 'Module Documents', text: 'Bons de livraison, photos, certificats OHADA centralisés.' },
        { label: 'Module Rapports', text: 'KPIs personnalisés, exports automatisés, tableaux de bord.' },
      ],
    },
    {
      type: 'quiz',
      title: 'Quiz final',
      question: 'Vous avez 5 demandes à créer pour le même client, sur le même trajet, avec des dates différentes. La méthode la plus efficace ?',
      options: [
        'Saisir manuellement les 5 demandes une par une',
        'Créer une demande, puis utiliser « Dupliquer » 4 fois et ajuster les dates',
        'Demander à l\'opérateur de les créer pour vous',
        'Utiliser l\'import CSV (réservé aux admins)',
      ],
      answerIndex: 1,
      explanation: 'La fonction Dupliquer est conçue exactement pour ce cas. Gain de temps × 5.',
    },
    {
      type: 'cta',
      title: 'Module 02 terminé !',
      subtitle: 'Vous maîtrisez maintenant la gestion des opérations. Continuez avec le module suivant ou pratiquez tout de suite.',
      actions: [
        { label: 'Créer ma première demande', href: '/nouvelle-demande' },
        { label: 'Voir mes opérations', href: '/operations' },
        { label: 'Module suivant', href: '/docs/client/training' },
      ],
    },
  ],
};

export default moduleClientMesOperations;
